import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef, useState } from 'react';
import { TransitionPresence } from 'util/react-transition-component';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import type { ShapeType, SuperpowersProps, SuperpowersRef } from './Superpowers.data';
import { superpowerConfig } from './Superpowers.data';
import styles from './Superpowers.module.scss';
import SuperpowerSlideBackground from './superpower-slide-background/SuperpowerSlideBackground';
import type { SuperpowerSlideBackgroundRef } from './superpower-slide-background/SuperpowerSlideBackground.data';
import SuperpowerSlide from './superpower-slide/SuperpowerSlide';
import type { SuperpowerSlideRef } from './superpower-slide/SuperpowerSlide.data';
import SuperpowerWebgl from './superpower-webgl/SuperpowerWebgl';
import type { SuperpowerWebglRef } from './superpower-webgl/SuperpowerWebgl.data';

export default function Superpowers({
  blok,
  ref,
  ...props
}: SuperpowersProps & { ref?: Ref<SuperpowersRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const keyPrefix = blok._uid;
  const [activeSlide, setActiveSlide] = useState(-1);
  const webglRef = useRef<SuperpowerWebglRef>(null);
  const slideBackgroundRef = useRef<SuperpowerSlideBackgroundRef>(null);
  const powerSlideRef = useRef<SuperpowerSlideRef>(null);

  const progressData = useRef<Array<{ id: ShapeType; progress: number }>>(
    blok.slides.map((slide) => ({
      id: slide.animation_type,
      progress: 0,
    })),
  );

  // useScrollTransition({
  //   scroll: {
  //     trigger: elementRef,
  //     vars: {},
  //   },
  //   setupOptions: () => ({
  //     refs: {
  //       elementRef,
  //     },
  //     setupTransitionInTimeline,
  //   }),
  // });

  useScrollTrigger({
    trigger: elementRef,
    endTrigger: wrapperRef,
    pin: wrapperRef,
    vars: {
      start: 'top top',
      end: `bottom bottom-=${blok.slides.length * 100 * 4}%`,
      pinType: 'fixed',
      anticipatePin: 1,
      scrub: true,
      onUpdate: ({ progress }) => {
        const totalSlides = blok.slides.length;
        const slideDuration = 1 / totalSlides;
        let slideIndex = Math.max(-1, progress > 0 ? Math.floor(progress / slideDuration) : -1);

        if (slideIndex > totalSlides - 1) {
          slideIndex = -1;
        }

        const slideProgress = (progress - slideIndex * slideDuration) / slideDuration;
        // setActiveSlide(slideIndex);
        if (activeSlide !== slideIndex) {
          setActiveSlide(slideIndex);
          if (webglRef?.current) webglRef.current.setCurrentSlide(slideIndex);
        }

        if (slideIndex > -1) {
          progressData.current[slideIndex].progress = slideProgress;
          if (webglRef?.current) webglRef.current.setProgressData(progressData.current);
          if (slideBackgroundRef?.current)
            slideBackgroundRef.current.setProgress(slideProgress, slideIndex);
          if (powerSlideRef?.current) powerSlideRef.current.setProgress(slideProgress, slideIndex);
        }

        if (webglRef.current) webglRef.current.setProgress(progress);
      },
    },
  });

  // console.log('blok.slides', blok.slides);
  const currentSlide = blok.slides[activeSlide];

  // if (activeSlide >= 0) {
  //   console.log('currentSlide', currentSlide);
  // }

  return (
    <div
      ref={elementRef}
      className={classNames(styles.superpowers, props.className)}
      {...sbEditable(blok)}
    >
      <div ref={wrapperRef} className={classNames(styles.wrapper)}>
        <div
          className={classNames(styles.slideBackgroundHolder)}
          style={
            {
              '--background-color':
                activeSlide >= 0
                  ? superpowerConfig[currentSlide.animation_type].backgroundColor
                  : '',
            } as React.CSSProperties
          }
        >
          <TransitionPresence>
            {activeSlide >= 0 && (
              <SuperpowerSlideBackground
                ref={slideBackgroundRef}
                key={`${keyPrefix}_slide_bg_${currentSlide._uid}`}
                blok={currentSlide}
                config={superpowerConfig[currentSlide.animation_type]}
                className={classNames(styles.backgroundSlide)}
                slideIndex={activeSlide}
                transitionOnMount
                transitionOnUnMount
              />
            )}
          </TransitionPresence>
        </div>

        <div className={classNames(styles.webglContainer)}>
          <SuperpowerWebgl
            ref={webglRef}
            slides={blok.slides.map((slide) => ({
              id: slide.animation_type,
              background_title: slide.background_title,
              ...superpowerConfig[slide.animation_type],
            }))}
          />
        </div>

        <div className={classNames(styles.slideHolder)}>
          <TransitionPresence>
            {activeSlide >= 0 && (
              <SuperpowerSlide
                ref={powerSlideRef}
                key={`${keyPrefix}_slide_${currentSlide._uid}`}
                blok={currentSlide}
                config={superpowerConfig[currentSlide.animation_type]}
                className={classNames(styles.slide)}
                slideIndex={activeSlide}
                transitionOnMount
                transitionOnUnMount
              />
            )}
          </TransitionPresence>
        </div>
      </div>
    </div>
  );
}
